/**
 * 貨品新增 / 編輯頁(REQ-20260520 ERP Phase 1C)。
 * 5 個 section:基本資料 / 採購 / 售價(分級)/ 成本(僅 ADMIN)/ 庫存控制 / 最近交易(只讀)。
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateProductSchema, type CreateProductInput } from '@app/shared';
import {
  useCreateProduct,
  useDeleteProduct,
  useProduct,
  useUpdateProduct,
} from '../features/products/api';
import { useReferences } from '../features/settings/api';
import { useSuppliers } from '../features/suppliers/api';
import { useAuthStore } from '../features/auth/store';

// 對齊 CustomerForm / SupplierForm:emerald 配色 + 密集 12-col
const inputCls =
  'w-full border border-slate-300 rounded px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 disabled:bg-slate-100 disabled:text-slate-500';
const labelCls = 'text-xs font-bold text-emerald-800';

const SPAN_CLS: Record<number, string> = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  7: 'md:col-span-7',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  10: 'md:col-span-10',
  11: 'md:col-span-11',
  12: 'md:col-span-12',
};

export function ProductFormPage(): JSX.Element {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = params.id ? Number(params.id) : undefined;
  const isEdit = id != null;
  const me = useAuthStore((s) => s.user);
  const isAdmin = me?.role === 'ADMIN';

  const { data: detail, isLoading: detailLoading } = useProduct(id);
  const { data: categories } = useReferences('product-categories', false);
  const { data: units } = useReferences('product-units', false);
  const { data: warehouses } = useReferences('warehouses', false);
  const { data: suppliers } = useSuppliers({ page: 1, pageSize: 100, sort: 'simpleName', order: 'asc' });

  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct(id ?? 0);
  const deleteMutation = useDeleteProduct();

  const [savedFlash, setSavedFlash] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /**
   * 回清單走瀏覽器的上一頁而不是 `navigate('/products')`(REQ-20260820006)——
   * 清單的搜尋 / 類別 / 頁碼現在存在網址上,跳到 `/products` 頂層等於把它們全部清掉,
   * 使用者得重篩一次。這裡沒有列印頁那個顧慮(REQ-20260811005 §四:上一頁是
   * 已存檔的編輯表單),上一頁就是那份清單。
   * 新增成功後導向編輯頁用的是 `replace`,所以那條路徑的上一頁也還是清單。
   */
  const goBackToList = (): void => navigate(-1);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateProductInput>({
    resolver: zodResolver(CreateProductSchema),
    defaultValues: {
      code: '',
      name: '',
      trackStock: true,
      safetyEnabled: false,
    },
  });

  useEffect(() => {
    if (detail) {
      reset({
        code: detail.code,
        name: detail.name,
        spec: detail.spec ?? '',
        usage: detail.usage ?? '',
        kind: detail.kind ?? '',
        barcode: detail.barcode ?? '',
        oemCode: detail.oemCode ?? '',
        categoryId: detail.categoryId ?? undefined,
        unitId: detail.unitId ?? undefined,
        defaultSupplierId: detail.defaultSupplierId ?? undefined,
        defaultWarehouseId: detail.defaultWarehouseId ?? undefined,
        listPrice: detail.listPrice ?? undefined,
        marketPrice: detail.marketPrice ?? undefined,
        priceTierA: detail.priceTierA ?? undefined,
        priceTierB: detail.priceTierB ?? undefined,
        priceTierC: detail.priceTierC ?? undefined,
        priceTierD: detail.priceTierD ?? undefined,
        priceTierE: detail.priceTierE ?? undefined,
        costStandard: detail.costStandard ?? undefined,
        costCurrent: detail.costCurrent ?? undefined,
        costMonthAvg: detail.costMonthAvg ?? undefined,
        trackStock: detail.trackStock,
        safetyEnabled: detail.safetyEnabled,
        safetyStock: detail.safetyStock ?? undefined,
        notes: detail.notes ?? '',
      });
    }
  }, [detail, reset]);

  const onSubmit = async (raw: CreateProductInput): Promise<void> => {
    setSubmitError(null);
    setSavedFlash(null);
    const cleaned: CreateProductInput = {
      ...raw,
      spec: raw.spec?.toString().trim() || null,
      usage: raw.usage?.toString().trim() || null,
      kind: raw.kind?.toString().trim() || null,
      barcode: raw.barcode?.toString().trim() || null,
      oemCode: raw.oemCode?.toString().trim() || null,
      notes: raw.notes?.toString().trim() || null,
    };
    try {
      if (isEdit && id != null) {
        await updateMutation.mutateAsync(cleaned);
        setSavedFlash('已儲存');
      } else {
        const created = await createMutation.mutateAsync(cleaned);
        navigate(`/products/${created.id}/edit`, { replace: true });
      }
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : '儲存失敗');
    }
  };

  const onDelete = async (): Promise<void> => {
    if (!isEdit || id == null) return;
    if (!window.confirm(`確定刪除「${detail?.name ?? '此貨品'}」?資料會軟刪除可恢復`)) return;
    await deleteMutation.mutateAsync(id);
    goBackToList();
  };

  if (isEdit && detailLoading) {
    return <div className="text-slate-500">載入中...</div>;
  }

  return (
    <section>
      <header className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">
          {isEdit ? `編輯貨品 — ${detail?.name ?? ''}` : '新增貨品'}
          {detail?.code ? (
            <span className="ml-3 text-base font-normal text-slate-400">{detail.code}</span>
          ) : null}
        </h1>
        <div className="flex gap-2">
          {/* 走上一頁而不是連到 /products —— 理由見 goBackToList() */}
          <button
            type="button"
            onClick={goBackToList}
            className="border rounded px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100"
          >
            返回列表
          </button>
          {isEdit ? (
            <button
              type="button"
              onClick={() => void onDelete()}
              disabled={deleteMutation.isPending}
              className="border border-red-600 text-red-600 px-3 py-1.5 rounded hover:bg-red-50 text-sm"
            >
              {deleteMutation.isPending ? '刪除中…' : '刪除'}
            </button>
          ) : null}
          <button
            type="submit"
            form="product-form"
            disabled={isSubmitting}
            className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50 text-sm"
          >
            {isSubmitting ? '儲存中…' : '儲存'}
          </button>
        </div>
      </header>

      <form
        id="product-form"
        onSubmit={handleSubmit(onSubmit)}
        className="grid gap-3 bg-white border border-emerald-200 rounded p-3"
        data-testid="product-form"
      >
        <Section title="基本資料">
          <Field label="編號*" error={errors.code?.message} colSpan={3}>
            <input {...register('code')} className={inputCls} disabled={isEdit} />
          </Field>
          <Field label="品名*" error={errors.name?.message} colSpan={5}>
            <input {...register('name')} className={inputCls} />
          </Field>
          <Field label="類別" error={errors.categoryId?.message} colSpan={2}>
            <select {...register('categoryId')} className={inputCls}>
              <option value="">—</option>
              {(categories?.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </Field>
          <Field label="單位" error={errors.unitId?.message} colSpan={2}>
            <select {...register('unitId')} className={inputCls}>
              <option value="">—</option>
              {(units?.data ?? []).map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </Field>
          <Field label="規格" error={errors.spec?.message} colSpan={4}>
            <input {...register('spec')} className={inputCls} />
          </Field>
          <Field label="用途" error={errors.usage?.message} colSpan={4}>
            <input {...register('usage')} className={inputCls} />
          </Field>
          <Field label="貨品型態" error={errors.kind?.message} colSpan={4}>
            <input {...register('kind')} className={inputCls} placeholder="一般 / 原料 / 半成品..." />
          </Field>
          <Field label="條碼" error={errors.barcode?.message} colSpan={6}>
            <input {...register('barcode')} className={inputCls} />
          </Field>
          <Field label="原廠貨號 / OEM" error={errors.oemCode?.message} colSpan={6}>
            <input {...register('oemCode')} className={inputCls} />
          </Field>
        </Section>

        <Section title="採購">
          <Field label="預設廠商" error={errors.defaultSupplierId?.message} colSpan={6}>
            <select {...register('defaultSupplierId')} className={inputCls}>
              <option value="">—</option>
              {(suppliers?.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.simpleName}{s.code ? ` (${s.code})` : ''}
                </option>
              ))}
            </select>
          </Field>
          <Field label="預設倉庫" error={errors.defaultWarehouseId?.message} colSpan={6}>
            <select {...register('defaultWarehouseId')} className={inputCls}>
              <option value="">—</option>
              {(warehouses?.data ?? []).map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </Field>
        </Section>

        <Section title="售價(5 級分)">
          <Field label="標準售價" error={errors.listPrice?.message} colSpan={2}>
            <input {...register('listPrice')} className={inputCls} placeholder="例:600" />
          </Field>
          <Field label="牌價" error={errors.marketPrice?.message} colSpan={2}>
            <input {...register('marketPrice')} className={inputCls} placeholder="例:800" />
          </Field>
          <Field label="售價 A" error={errors.priceTierA?.message} colSpan={2}>
            <input {...register('priceTierA')} className={inputCls} />
          </Field>
          <Field label="售價 B" error={errors.priceTierB?.message} colSpan={2}>
            <input {...register('priceTierB')} className={inputCls} />
          </Field>
          <Field label="售價 C" error={errors.priceTierC?.message} colSpan={2}>
            <input {...register('priceTierC')} className={inputCls} />
          </Field>
          <Field label="售價 D" error={errors.priceTierD?.message} colSpan={2}>
            <input {...register('priceTierD')} className={inputCls} />
          </Field>
          <Field label="售價 E" error={errors.priceTierE?.message} colSpan={2}>
            <input {...register('priceTierE')} className={inputCls} />
          </Field>
        </Section>

        {isAdmin ? (
          <Section title="成本(僅 ADMIN 可見可改)">
            <Field label="標準成本" error={errors.costStandard?.message} colSpan={4}>
              <input {...register('costStandard')} className={inputCls} />
            </Field>
            <Field label="現行成本" error={errors.costCurrent?.message} colSpan={4}>
              <input {...register('costCurrent')} className={inputCls} />
            </Field>
            <Field label="月平均成本" error={errors.costMonthAvg?.message} colSpan={4}>
              <input {...register('costMonthAvg')} className={inputCls} />
            </Field>
          </Section>
        ) : (
          <div className="text-xs text-slate-500 bg-slate-50 border rounded p-2">
            成本欄位僅 ADMIN 角色可見可改;你目前是 SALES / VIEWER。
          </div>
        )}

        <Section title="庫存控制">
          <div className="md:col-span-12 flex flex-wrap gap-6 items-end text-sm">
            <label className="inline-flex items-center gap-2 text-emerald-800">
              <input type="checkbox" {...register('trackStock')} /> 計算庫存
            </label>
            <label className="inline-flex items-center gap-2 text-emerald-800">
              <input type="checkbox" {...register('safetyEnabled')} /> 考慮安全存量
            </label>
            <label className="grid gap-0.5">
              <span className={labelCls}>安全存量</span>
              <input
                {...register('safetyStock')}
                className={inputCls}
                placeholder="例:100"
              />
              {errors.safetyStock?.message ? (
                <span role="alert" className="text-xs text-red-600">
                  {errors.safetyStock.message}
                </span>
              ) : null}
            </label>
          </div>
        </Section>

        {isEdit && detail ? (
          <Section title="最近交易(系統自動更新,只讀)">
            <ReadOnly label="最近進貨日" colSpan={4}>
              {detail.lastPurchaseAt ? new Date(detail.lastPurchaseAt).toLocaleDateString('zh-TW') : '—'}
            </ReadOnly>
            <ReadOnly label="最近出貨日" colSpan={4}>
              {detail.lastSaleAt ? new Date(detail.lastSaleAt).toLocaleDateString('zh-TW') : '—'}
            </ReadOnly>
            <ReadOnly label="最近售價" colSpan={4}>{detail.lastSalePrice ?? '—'}</ReadOnly>
          </Section>
        ) : null}

        <Section title="備註">
          <Field label="" colSpan={12}>
            <textarea {...register('notes')} rows={3} className={inputCls} />
          </Field>
        </Section>

        {submitError ? (
          <div role="alert" className="text-red-700 bg-red-50 border border-red-200 rounded p-2 text-sm">
            {submitError}
          </div>
        ) : null}
        {savedFlash ? (
          <div role="status" className="text-green-700 text-sm">{savedFlash}</div>
        ) : null}
      </form>
    </section>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="grid gap-1">
      <h2 className="text-xs font-bold text-emerald-700 border-b border-emerald-100 pb-0.5">
        {title}
      </h2>
      <div className="grid gap-x-2 gap-y-2 md:grid-cols-12 text-sm pt-1">
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  colSpan = 4,
  children,
}: {
  label: string;
  error?: string | undefined;
  colSpan?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  children: React.ReactNode;
}): JSX.Element {
  const span = SPAN_CLS[colSpan] ?? '';
  return (
    <label className={`grid gap-0.5 ${span}`}>
      <span className={labelCls}>{label}</span>
      {children}
      {error ? (
        <span role="alert" className="text-xs text-red-600">
          {error}
        </span>
      ) : null}
    </label>
  );
}

function ReadOnly({
  label,
  children,
  colSpan = 4,
}: {
  label: string;
  children: React.ReactNode;
  colSpan?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
}): JSX.Element {
  const span = SPAN_CLS[colSpan] ?? '';
  return (
    <label className={`grid gap-0.5 ${span}`}>
      <span className={labelCls}>{label}</span>
      <div className="border border-slate-200 rounded px-2 py-1 text-sm bg-slate-50 text-slate-600">
        {children}
      </div>
    </label>
  );
}
